import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Box, LinearProgress, Paper, Typography } from '@material-ui/core';
import DoneIcon from '@material-ui/icons/Done';
import ClearIcon from '@material-ui/icons/Clear';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      color: 'rgba(0, 0, 0, 0.87)',
      fontSize: '20px',
      lineHeight: '24px',
      fontWeight: theme.typography.fontWeightMedium
    },
    progressGreen: {
      backgroundColor: 'rgba(0, 149, 60, 0.23)',
      '& > *': { backgroundColor: '#00953B' }
    },
    progressRed: {
      backgroundColor: 'rgba(176, 0, 32, 0.23)',
      '& > *': { backgroundColor: '#B00020' }
    },
    progressYellow: {
      backgroundColor: 'rgba(255, 160, 0, 0.23)',
      '& > *': { backgroundColor: '#FFA000' }
    },
    container: {
      padding: theme.spacing(3)
    },
    sections: {
      margin: theme.spacing(2, 0)
    },
    statusIcon: {
      marginRight: 8,
      fontSize: 16
    },
    sectionName: {
      color: '#333333'
    },
    link: {
      display: 'flex',
      alignItems: 'center',
      color: '#006E96',
      fontSize: '16px'
    }
  })
);

const getProgressColor = (progress: number): string => {
  if (progress === 100) return 'progressGreen';
  if (progress === 0) return 'progressRed';
  return 'progressYellow';
};

type Props = {
  entries: [string, boolean | null][];
  organizationId?: string;
};

export const SectionsStatus: React.FC<Props> = ({ entries, organizationId }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const sections = [
    {
      key: 'operationRegionsIsCompleted',
      title: t('components.sectionsStatus.operatingRegions', 'Select your Operating Regions')
    },
    {
      key: 'operatingRegionsV2IsCompleted',
      title: t('components.sectionsStatus.operatingRegions', 'Select your Operating Regions')
    },
    {
      key: 'industriesIsCompleted',
      title: t('components.sectionsStatus.industries', 'Select Industries in which your company work')
    },
    {
      key: 'servicesIsCompleted',
      title: t('components.sectionsStatus.services', 'Specify Services your company provides')
    },
    {
      key: 'serviceCategoriesIsCompleted',
      title: t('components.sectionsStatus.services', 'Specify Services your company provides')
    },
    {
      key: 'allServicesMatchBusinessType',
      title: t(
        'components.sectionsStatus.matchingServices',
        'Some work categories and/or services assigned to your account do not match with your new Business type'
      )
    }
  ];

  return (
    <Paper variant="outlined">
      <LinearProgress className={classes[getProgressColor(50)]} variant="determinate" value={50} />
      <div className={classes.container}>
        <Typography className={classes.title}>
          {t('components.sectionsStatus.completeOperatingDetails', 'Complete your Operating Details first')}
        </Typography>
        <div className={classes.sections}>
          {sections.map((section, index) => {
            const entry = entries.find(entry => entry[0] === section.key);
            if (!entry) {
              return null;
            }
            return (
              <Box key={index} display="flex" alignItems="center">
                {entry[1] === true && <DoneIcon color="primary" fontSize="small" className={classes.statusIcon} />}
                {entry[1] === false && <ClearIcon color="error" fontSize="small" className={classes.statusIcon} />}
                <Typography className={classes.sectionName}>{section.title}</Typography>
              </Box>
            );
          })}
        </div>
        <a className={classes.link} href={`/organizations/${organizationId}/profile`}>
          {t('components.sectionsStatus.continueToOperatingDetails', 'Continue to Operating Details')}
          <NavigateNextIcon />
        </a>
      </div>
    </Paper>
  );
};
